/**
 * Service › Drinks: the book as a card index, for behind the bar.
 *
 * The Items screen answers a manager's question — what the book holds, what
 * it costs, what is retired — and does it with filters and a table. At
 * 11pm the question is "how do I make a Paper Plane", and the answer is one
 * card: the working recipe, as poured. So this is the same book dealt out
 * as cards, and it carries no money: cost and GP are screen 07's business
 * and everyone at the venue can see this one.
 *
 * Three ways through it, because a bartender's hands are full:
 *
 *   - flip      the cards snap one to a view; a thumb scrolls the deck
 *   - swipe     a drink with variants swipes sideways between them
 *   - rolodex   the A–Z rail down the edge jumps to a letter; drag it and
 *               the deck runs under your thumb the way a card index does
 *
 * The deck's order and its letters are `deck.ts` in @baros/core. What lives
 * here is the scrolling, which is CSS scroll-snap doing the work — a native
 * scroll keeps its momentum, its rubber band and its accessibility, where a
 * hand-rolled drag loses all three.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  hasTalk,
  deckOf,
  HOUSE_LABEL,
  lettersOf,
  lineText,
  specTotals,
  variantLabel,
  type DeckCard,
  type Ingredient,
  type Recipe,
} from "@baros/core";
import { useApp } from "../context.js";
import { STATUS_LABEL, statusColour } from "../format.js";
import type { ScreenProps } from "../screens.js";
import "./drinks.css";

/** What a card needs of an ingredient, by id. */
type Ingredients = ReadonlyMap<string, Ingredient>;

export function DrinksScreen(_props: ScreenProps) {
  const app = useApp();
  const [query, setQuery] = useState("");
  const deckRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);

  const ingredients: Ingredients = useMemo(
    () => new Map(app.library.ingredients.map((i) => [i.ingredientId, i])),
    [app.library.ingredients],
  );

  // Drinks, not food: a plate has no working recipe to swipe between and no
  // pour to read. Food stays on Items, which still holds the whole book.
  const drinks = useMemo(
    () => (app.library.items ?? []).filter((i) => i.kind === "drink"),
    [app.library.items],
  );
  const recipes = app.library.recipes ?? [];
  // A kitchen's items are still in the book, and Service used to list them
  // here. The deck is drinks, so say where the rest went rather than let a
  // venue with food find half its menu missing.
  const food = (app.library.items ?? []).filter((i) => i.kind === "food").length;

  const all = useMemo(() => deckOf(drinks, recipes), [drinks, recipes]);

  const q = query.trim().toLowerCase();
  const cards = useMemo(
    () =>
      !q
        ? all
        : all.filter(
            (c) =>
              c.name.toLowerCase().includes(q) ||
              c.recipes.some(
                (r) =>
                  r.label.toLowerCase().includes(q) ||
                  r.lines.some((l) => ingredients.get(l.ingredientId)?.name.toLowerCase().includes(q)),
              ),
          ),
    [all, q, ingredients],
  );

  const letters = useMemo(() => lettersOf(cards), [cards]);
  const [here, setHere] = useState(0);

  // Which card is under the eye, for the rail's lit letter. An observer
  // rather than a scroll handler: it fires once a card settles instead of
  // on every frame of a flick.
  useEffect(() => {
    const deck = deckRef.current;
    if (!deck || cards.length === 0) return;
    const seen = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const at = top && Number((top.target as HTMLElement).dataset["at"]);
        if (typeof at === "number" && Number.isFinite(at)) setHere(at);
      },
      { root: deck, threshold: [0.5, 0.9] },
    );
    cardRefs.current.length = cards.length;
    for (const el of cardRefs.current) if (el) seen.observe(el);
    return () => seen.disconnect();
  }, [cards]);

  /**
   * Instant, not smooth, and it matters: a mandatory snap container stops a
   * smooth programmatic scroll at the first snap point it passes, so a tap
   * on a far letter glided to the card *before* the one it named. Flipping
   * to a tab on a card index is instant anyway — nobody watches a rolodex
   * travel — so this is the right feel as well as the working one.
   */
  const jump = useCallback((index: number) => {
    cardRefs.current[index]?.scrollIntoView({ block: "start" });
  }, []);

  /**
   * Dragging the rail runs the deck under the thumb, which is the whole
   * point of a rolodex. Pointer events rather than touch: one path covers a
   * thumb, a mouse and a stylus, and capturing the pointer keeps the drag
   * alive when the finger slides off the rail.
   *
   * The letter is read off the buttons' own geometry — the nearest one to
   * the pointer — rather than from the pointer's share of the rail's
   * height. The rail is centred in a column taller than itself, so a share
   * of the whole is a letter or two out, and capturing the pointer means
   * the button never gets the click that would have corrected it.
   */
  const railRef = useRef<HTMLDivElement>(null);
  const toLetterAt = useCallback((clientY: number) => {
    const rail = railRef.current;
    if (!rail) return;
    let nearest: { at: number; gap: number } | null = null;
    for (const button of rail.querySelectorAll<HTMLElement>("button[data-at]")) {
      const box = button.getBoundingClientRect();
      const gap = Math.abs(clientY - (box.top + box.height / 2));
      const at = Number(button.dataset["at"]);
      if (Number.isFinite(at) && (!nearest || gap < nearest.gap)) nearest = { at, gap };
    }
    // Instant for the same reason `jump` is, and because a drag asks for
    // the deck to follow the thumb rather than chase it.
    if (nearest) cardRefs.current[nearest.at]?.scrollIntoView({ block: "start" });
  }, []);

  const empty = all.length === 0;

  return (
    <div className="screen dk">
      <div className="row screen-head dk-head">
        <div>
          <div className="label">
            {empty ? "nothing on file" : `${all.length} drink${all.length === 1 ? "" : "s"} on file`}
          </div>
          <div className="dk-count">{empty ? "Empty" : `${cards.length} shown`}</div>
        </div>
        <span className="spacer" />
        {food > 0 && (
          <button type="button" className="dk-food label" onClick={() => app.go({ kind: "recipes" })}>
            {food} food {food === 1 ? "item" : "items"} on Items →
          </button>
        )}
        {!empty && (
          <input
            type="search"
            className="dk-search"
            placeholder="Find a drink or an ingredient"
            aria-label="Find a drink or an ingredient"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}
      </div>

      {empty ? (
        <div className="pad">
          <div className="msg">
            <div style={{ fontWeight: 600 }}>No drinks on file yet.</div>
            <div style={{ color: "var(--t2)", marginTop: 4 }}>
              Add one under Development › Items and it appears here as a card.
            </div>
          </div>
        </div>
      ) : cards.length === 0 ? (
        <div className="pad">
          <div className="label">Nothing matches “{query.trim()}”.</div>
        </div>
      ) : (
        <div className="dk-body">
          <div className="dk-deck" ref={deckRef}>
            {cards.map((card, i) => (
              <DrinkCard
                key={card.itemId}
                card={card}
                at={i}
                ingredients={ingredients}
                onOpen={(recipeId) => app.go({ kind: "recipes", params: { specId: recipeId } })}
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
              />
            ))}
            {/* The last card can snap to the top like every other one. */}
            <div className="dk-tail" aria-hidden="true" />
          </div>

          <div
            className="dk-rail"
            ref={railRef}
            role="toolbar"
            aria-label="Jump to a letter"
            aria-orientation="vertical"
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              toLetterAt(e.clientY);
            }}
            onPointerMove={(e) => {
              if (e.currentTarget.hasPointerCapture(e.pointerId)) toLetterAt(e.clientY);
            }}
          >
            {letters.map((l) => (
              <button
                key={l.letter}
                type="button"
                className="dk-letter"
                data-at={l.index}
                aria-current={cards[here]?.letter === l.letter}
                // A pointer is handled by the rail, which has already moved
                // the deck by the time this could fire. This is for the
                // keyboard, where there is no pointer to read a position
                // from — and it is why the letters are buttons at all.
                onClick={() => jump(l.index)}
              >
                {l.letter}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface CardProps {
  readonly card: DeckCard<Recipe>;
  /** Its place in the deck, which the rail scrolls to and the observer reads. */
  readonly at: number;
  readonly ingredients: Ingredients;
  readonly onOpen: (recipeId: string) => void;
  readonly ref: (el: HTMLElement | null) => void;
}

/**
 * One drink.
 *
 * The initial is set as a drop cap, large enough to read at arm's length
 * down a scrolling deck — it is the card's tab, which is what makes the
 * rail worth dragging. It is `aria-hidden` and the name is written out in
 * full beside it, so a screen reader hears "Negroni" and not "N, egroni".
 */
function DrinkCard({ card, at, ingredients, onOpen, ref }: CardProps) {
  const [shown, setShown] = useState(0);
  const strip = useRef<HTMLDivElement>(null);
  const recipe = card.recipes[shown] ?? card.recipes[0];
  const many = card.recipes.length > 1;

  const go = (next: number) => {
    const to = Math.min(card.recipes.length - 1, Math.max(0, next));
    setShown(to);
    const panel = strip.current?.children[to];
    if (panel instanceof HTMLElement) panel.scrollIntoView({ inline: "start", block: "nearest", behavior: "smooth" });
  };

  // A swipe moves the strip, not the state; this reads back which panel it
  // settled on so the dots and the arrows agree with what is on screen.
  const settled = () => {
    const el = strip.current;
    if (!el) return;
    const to = Math.min(card.recipes.length - 1, Math.max(0, Math.round(el.scrollLeft / Math.max(1, el.clientWidth))));
    // A swipe fires this every frame; only the frame that crosses into the
    // next panel has anything to say.
    setShown((was) => (was === to ? was : to));
  };

  if (!recipe) return null;

  return (
    <article className="dk-card" data-at={at} ref={ref} aria-label={card.name}>
      <header className="dk-card-head">
        {/* The drop cap is the name's own first letter, set large by
            `::first-letter` rather than drawn beside it — the rail's tab
            may differ ("Épices" files under E, "7 & 7" under #) and two
            letters where the name has one is a stutter. No markup for it,
            so the name is still one word to a screen reader. */}
        <h2 className="dk-name">{card.name}</h2>
        <div className="dk-sub label">
          {recipe.house ? HOUSE_LABEL : variantLabel(card.name, recipe.label) || recipe.label}
          {recipe.status !== "available" && (
            <>
              {" · "}
              <span style={{ color: statusColour(recipe.status) }}>{STATUS_LABEL[recipe.status]}</span>
            </>
          )}
        </div>
      </header>

      <div className="dk-strip" ref={strip} onScroll={settled}>
        {card.recipes.map((r) => (
          <RecipePanel key={r.recipeId} recipe={r} ingredients={ingredients} onOpen={onOpen} />
        ))}
      </div>

      {many && (
        <footer className="dk-variants">
          <button
            type="button"
            className="dk-arrow"
            aria-label="The recipe before"
            disabled={shown === 0}
            onClick={() => go(shown - 1)}
          >
            ‹
          </button>
          <div className="dk-dots" role="tablist" aria-label={`${card.name} recipes`}>
            {card.recipes.map((r, i) => (
              <button
                key={r.recipeId}
                type="button"
                role="tab"
                className="dk-dot"
                aria-selected={i === shown}
                aria-label={r.house ? HOUSE_LABEL : variantLabel(card.name, r.label) || r.label}
                onClick={() => go(i)}
              />
            ))}
          </div>
          <button
            type="button"
            className="dk-arrow"
            aria-label="The recipe after"
            disabled={shown === card.recipes.length - 1}
            onClick={() => go(shown + 1)}
          >
            ›
          </button>
        </footer>
      )}
    </article>
  );
}

/** The working recipe as poured: what goes in, then how it is served. */
function RecipePanel(p: {
  readonly recipe: Recipe;
  readonly ingredients: Ingredients;
  readonly onOpen: (recipeId: string) => void;
}) {
  const { recipe, ingredients } = p;
  const totals = specTotals({ lines: recipe.lines, sundries: recipe.sundries }, ingredients);
  const serve = [recipe.glass, recipe.method, recipe.ice].map((s) => s.trim()).filter(Boolean);

  return (
    <section className="dk-panel">
      {recipe.lines.length > 0 ? (
        <ol className="dk-lines">
          {recipe.lines.map((l, i) => (
            <li key={i} className={l.batched ? "batched" : undefined}>
              {lineText(l, ingredients)}
            </li>
          ))}
        </ol>
      ) : (
        <div className="label dk-none">No build on file for this recipe.</div>
      )}

      {hasTalk(recipe.talk) && (
        <details className="dk-talk">
          <summary className="dk-one">{recipe.talk.one || recipe.talk.two || recipe.talk.three}</summary>
          {recipe.talk.one && recipe.talk.two && <p>{recipe.talk.two}</p>}
          {recipe.talk.three && <p>{recipe.talk.three}</p>}
        </details>
      )}
      {serve.length > 0 && <div className="dk-serve">{serve.join(" · ")}</div>}
      {recipe.garnish.trim() && <div className="dk-garnish">Garnish: {recipe.garnish.trim()}</div>}

      {/* This recipe's own tags, not the drink's: what is true of this way
          of making it — "summer list", "batch only" (#188). */}
      {(recipe.tags ?? []).length > 0 && (
        <div className="dk-tags">
          {(recipe.tags ?? []).map((t) => (
            <span key={t} className="label tag-pill">
              {t}
            </span>
          ))}
        </div>
      )}

      <div className="dk-facts label">
        {totals.volumeMl > 0 ? `${Math.round(totals.volumeMl)} mL · ${totals.abv.toFixed(1)}% ABV` : ""}
        <button type="button" className="dk-open" onClick={() => p.onOpen(recipe.recipeId)}>
          Open the full recipe →
        </button>
      </div>
    </section>
  );
}
